import { useRouter } from '@abyss/web/hooks/useRouter';
import { config } from '@abyss/web/tools/config';
import { storage } from '@abyss/web/tools/storage';
import { LoadingSpinner } from '@abyss/web/ui/LoadingSpinner';
import { useContext, useEffect } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { adobeImpressionTrackEvent } from '../../../../common/AdobeTagging/adobeImpressionTrackEvent';
import {
  Constants,
  LOB,
  ReverseCoverageTypesCodes,
  SIGN_IN_PARAM,
} from '../../../../common/Constants';
import { LOADER_CENTER_STYLES } from '../../../../common/ConstantsStyles';
import { PortalName } from '../../../../common/Portal';
import { setGeoLocationToStorage } from '../../../../common/PSXHeader/SearchBar/utils';
import { PortalContext } from '../../../../context/PortalContext';
import { LagoonProvider } from '../../../../lagoon';
import { getLOBTargetInCookies } from '../../../../lagoon/lagoonUtil';
import { useChipStore } from '../../../../store/useChipStore';
import { ChipState } from '../../../../store/useChipStore/chipStore';
import { useGuestStore } from '../../../../store/useGuestStore';
import { GuestState } from '../../../../store/useGuestStore/guestStore';
import { DateTime } from '../../../../utils/DateTime';
import { isLawwGuest } from '../../../GuestExperience/utils';
import { IChildrenProp } from '../../types';
import { getLanguage } from '../Internationalization/helpers';
import { Locale } from '../Internationalization/types';

const parsedLob = (lineOfBusiness: string, defaultValue: string) => {
  switch (lineOfBusiness?.toLowerCase()) {
    case 'e&i':
    case 'eni':
      return 'eni';
    case 'm&r':
    case 'mnr':
      return 'mnr';
    case 'c&s':
    case 'cns':
    case 'c&s-dsnp':
      return 'cns';
    case 'ifp':
      return 'ifp';
    case 'obh':
      return 'obh';
    case 'umr':
      return 'umr';
    case 'studentresources':
    case 'student-resources':
      return 'student-resources';
    case 'flexwork':
    case 'flex-work':
      return 'flex-work';
    case 'uhone':
      return 'uhone';
    case 'guest': // Used for IFP Early Pay
      return 'guest';
    case 'guest-eni':
      return 'guest-eni';
    case 'guest-mnr':
      return 'guest-mnr';
    case 'guest-cns':
      return 'guest-cns';
    case 'guest-ifp':
      return 'guest-ifp';
    case 'guest-umr':
      return 'guest-umr';
    case 'guest-obh':
      return 'guest-obh';
    case 'guest-plan-selection':
      return 'guest-plan-selection';
    default:
      return defaultValue;
  }
};

const parsedApp = (lineOfBusiness) => {
  const appMapping = {
    eni: 'psx-web',
    mnr: 'psx-web-mnr',
    obh: 'psx-web-obh',
    cns: 'psx-web-cns',
    ifp: 'psx-web-ifp',
    umr: 'psx-web-umr',
    guest: 'psx-web-guest',
    'guest-eni': 'psx-web-guest-eni',
    'guest-mnr': 'psx-web-guest-mnr',
    'guest-cns': 'psx-web-guest-cns',
    'guest-ifp': 'psx-web-guest-ifp',
    'guest-umr': 'psx-web-guest-umr',
    'guest-obh': 'psx-web-guest-obh',
    'guest-plan-selection': 'psx-web-guest', // TODO: change to psx-web-guest-plan-selection when available
    'student-resources': 'psx-web-student-resources',
    'flex-work': 'psx-web-flex-work',
    uhone: 'psx-web-uhone',
  };

  const normalizedLOB = parsedLob(lineOfBusiness, '');
  const baseApp: string = appMapping[normalizedLOB] || '';

  return baseApp;
};

const mapPortalToLob = (portalName, portalDataLob?, isGuestUser = false) => {
  if (isGuestUser && isLawwGuest()) {
    return 'guest-obh';
  }

  if (
    isGuestUser &&
    portalName === PortalName.UMR &&
    portalDataLob?.toLowerCase() === 'e&i'
  ) {
    return 'guest-umr';
  }
  switch (portalName?.toLowerCase()) {
    case 'laww':
      return 'obh';
    case 'support_finder':
      return 'obh';
    case 'umr':
      return 'umr';
    case 'geha':
      return 'umr';
    case 'studentresources':
      return 'student-resources';
    case 'flexwork':
      return 'flex-work';
    case 'uhone':
      return 'uhone';
    case 'guest-plan-selection':
      switch (portalDataLob?.toLowerCase()) {
        case 'e&i':
          return 'guest-eni';
        case 'm&r':
          return 'guest-mnr';
        case 'c&s':
          return 'guest-cns';
        case 'ifp':
        case 'shop':
          return 'guest-ifp';
        default:
          return '';
      }
    default:
      return '';
  }
};

const getNormalizedLOB = (lobStr: string) => {
  if (!lobStr) {
    return '';
  }
  const key = lobStr.startsWith('GUEST-')
    ? lobStr.slice(6).toUpperCase()
    : lobStr.toUpperCase();

  return LOB[key] || key;
};
const getPotientiallySupportedLanguagesLocale = (lob): string[] =>
  getNormalizedLOB(lob.toUpperCase()) === LOB.CNS
    ? [
        Locale.enUS,
        Locale.esUS,
        Locale.myMM,
        Locale.htHT,
        Locale.zhTW,
        Locale.viVN,
        Locale.ilPH,
        Locale.koUS,
      ]
    : [Locale.enUS, Locale.esUS];

export interface Base64DeepLinkParams {
  language: string;
  lob?: string;
  coverageType?: string;
  reciprocityId?: string;
  clientName?: string;
  planName?: string;
  planId?: string;
  customBranding?: string;
  policyNumber?: string;
  fipsCode?: string;
  clusterKey?: string;
  portal?: string;
  pvrc?: string;
  signin?: string;
  planYear?: string;
  stateCode?: string;
  saveWithoutPlanValidation?: string;
}

const CLUSTER_KEY_PATTERN = /^RP\d{4}UH\d?$/;

const isValidClusterKey = (clusterKey?: string): boolean => {
  if (!clusterKey) {
    return false;
  }

  if (!CLUSTER_KEY_PATTERN.test(clusterKey)) {
    adobeImpressionTrackEvent({
      type: 'guest deeplink',
      message: `clusterKey malformed: ${clusterKey}`,
      method: 'getDeepLinkParams',
    });
    return false;
  }
  return true;
};

const getDeepLinkParams = (searchParams: URLSearchParams) => {
  const deepLinkBase64 = searchParams?.get('deeplink');
  let decodedParams: Base64DeepLinkParams | null = null;
  if (deepLinkBase64 && deepLinkBase64 !== 'true') {
    try {
      const parsed: unknown = JSON.parse(
        Buffer.from(deepLinkBase64, 'base64').toString('utf8')
      );
      decodedParams = parsed as Base64DeepLinkParams;
    } catch {
      throw new Error('Failed to decode deep link params');
    }
  }

  // Support both 'locale' and 'lang' params for backward compatibility
  const urlLocale =
    searchParams?.get('locale') ||
    searchParams?.get('lang') ||
    decodedParams?.language;
  const urlLOB = searchParams?.get('lob') || decodedParams?.lob;

  const urlSource =
    searchParams?.get('source') || (deepLinkBase64 ? 'deeplink' : undefined);
  // Support both 'coverageType' and 'CoverageType' for backward compatibility
  const urlCoverageType =
    searchParams?.get('coverageType') ||
    searchParams?.get('CoverageType') ||
    decodedParams?.coverageType;

  const currentYear = String(
    DateTime.getCurrentDateTimeWithCentralOffset().getFullYear()
  );

  let urlPlanYear = searchParams?.get('planYear');

  let urlDelsysCode =
    searchParams?.get('delsysCode') || decodedParams?.reciprocityId;

  if (urlPlanYear && urlPlanYear !== currentYear) {
    urlDelsysCode = undefined;
    urlPlanYear = currentYear; // Force year to current year for now
  } else if (
    urlDelsysCode === '0' &&
    urlCoverageType === 'B' &&
    urlLOB === 'EI'
  ) {
    urlDelsysCode = 'Employer Guest';
  }

  const urlSearch = searchParams?.get('search') || undefined;
  const signInParam = searchParams?.get('signin') || decodedParams?.signin;
  const urlSignIn =
    signInParam === SIGN_IN_PARAM.ENABLE ||
    signInParam === SIGN_IN_PARAM.DISABLE
      ? signInParam
      : undefined;
  const urlPlanName = searchParams?.get('planName') || decodedParams?.planName;
  const urlLagoonEnv = searchParams?.get('lagoonEnv');
  const urlPortal =
    searchParams?.get('portal') || decodedParams?.portal || undefined;
  const urlClientName =
    searchParams?.get('clientName') || decodedParams?.clientName;
  const isBrowsePage =
    window.location.pathname === '/guest-plan-selection/browse';
  const isGuestDeeplinkFlow = urlSource === 'deeplink' && isBrowsePage;
  //deep link does not include a planYear parameter it will be only append to the end of the encoded deeplink url
  const rawClusterKey = decodedParams?.clusterKey;
  const clusterKeyValid = isValidClusterKey(rawClusterKey);

  adobeImpressionTrackEvent({
    type: 'guest deeplink',
    message: rawClusterKey
      ? `clusterKey present: ${
          clusterKeyValid
            ? 'valid and tiering triggered'
            : 'invalid and tiering not triggered'
        }(${rawClusterKey})`
      : 'clusterKey absent',
    method: 'getDeepLinkParams',
  });

  return {
    isGuestDeeplinkFlow,
    urlLocale,
    urlLOB,
    urlDelsysCode,
    urlSource,
    urlCoverageType,
    urlSearch,
    urlSignIn,
    urlPlanName,
    urlLagoonEnv,
    urlPortal,
    urlClientName,
    urlPlanId: decodedParams?.planId,
    urlCustomBranding: decodedParams?.customBranding,
    urlPolicyNumber: decodedParams?.policyNumber,
    urlFipsCode: decodedParams?.fipsCode,
    urlClusterKey: clusterKeyValid ? rawClusterKey : undefined,
    urlPvrc: decodedParams?.pvrc,
    urlPlanYear,
    urlStateCode: decodedParams?.stateCode,
    urlsaveWithoutPlanValidation: decodedParams?.saveWithoutPlanValidation,
  };
};

export const Configuration = ({
  children,
  isWidget = false,
}: IChildrenProp) => {
  const { getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();
  const {
    isGuestDeeplinkFlow,
    urlLocale,
    urlLOB,
    urlDelsysCode,
    urlSource,
    urlCoverageType,
    urlSearch,
    urlSignIn,
    urlPlanName,
    urlLagoonEnv,
    urlPortal,
    urlClientName,
    urlPlanId,
    urlCustomBranding,
    urlPolicyNumber,
    urlFipsCode,
    urlClusterKey,
    urlPvrc,
    urlPlanYear,
    urlStateCode,
    urlsaveWithoutPlanValidation,
  } = getDeepLinkParams(searchParams);

  const [lob, setLob] = useSessionStorage<string>(
    Constants.STORAGE_KEYS.SESSION.LOB,
    ''
  );

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { locale: localeFromStorage, chipValue } = chipStore;

  const { guestPlanSelectorData, setGuestPlanSelectorData } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestPlanSelectorData: state.guestPlanSelectorData,
      setGuestPlanSelectorData: state.setGuestPlanSelectorData,
    }))
  );

  // Store lagoon env in session storage to persist across page navigations
  const [storedLagoonEnv, setStoredLagoonEnv] = useSessionStorage<string>(
    Constants.STORAGE_KEYS.SESSION.LAGOON_ENV,
    config('LAGOON_ENV') as string
  );

  // Use URL param if present, otherwise use stored value
  const lagoonEnv = urlLagoonEnv || storedLagoonEnv;

  // Update stored value when URL param is provided
  useEffect(() => {
    if (urlLagoonEnv && urlLagoonEnv !== storedLagoonEnv) {
      setStoredLagoonEnv(urlLagoonEnv);
    }
  }, [urlLagoonEnv, storedLagoonEnv, setStoredLagoonEnv]);

  useEffect(() => {
    if (urlLOB) {
      setLob(urlLOB);
    }
  }, [urlLOB]);

  useEffect(() => {
    if (urlLocale && isGuestPlanSelection) {
      storage.cookie.set(Constants.PSLOCALE, urlLocale);
    }
  }, [urlLocale]);

  // Store URL params directly in session storage for other components to access
  useEffect(() => {
    setGuestPlanSelectorData({
      ...guestPlanSelectorData,
      // Clear stale deeplink-only fields when not on a deeplink or guest-plan-selection route
      ...(!isGuestPlanSelection &&
        !urlSource && {
          clusterKey: undefined,
          delsysCode: undefined,
          isGuestDeeplinkFlow: false,
          search: undefined,
          source: undefined,
        }),
      ...(urlClusterKey && { clusterKey: urlClusterKey }),
      ...(urlCoverageType && { coverageType: urlCoverageType }),
      ...(urlCustomBranding && { customBranding: urlCustomBranding }),
      ...(urlDelsysCode && { delsysCode: urlDelsysCode }),
      ...(urlFipsCode && { fipsCode: urlFipsCode }),
      ...(urlSource && { isGuestDeeplinkFlow }),
      ...(urlPlanId && { planId: urlPlanId }),
      ...(urlPlanName && { planName: urlPlanName }),
      ...(urlPlanYear && { planYear: urlPlanYear }),
      ...(urlPolicyNumber && { policyNumber: urlPolicyNumber }),
      ...(urlPortal && { portal: urlPortal }),
      ...(urlPvrc && { pvrcode: urlPvrc }),
      ...(urlsaveWithoutPlanValidation && {
        saveWithoutPlanValidation: urlsaveWithoutPlanValidation === 'true',
      }),
      ...(urlSearch && { search: urlSearch }),
      ...(urlSignIn && { signIn: urlSignIn }),
      ...(urlSource && { source: urlSource }),
      ...(urlStateCode && { stateCode: urlStateCode }),
    });

    const sessionStorageParams = [
      {
        key: Constants.STORAGE_KEYS.SESSION.CLIENT_NAME,
        value: urlClientName,
      },
    ];

    sessionStorageParams.forEach(({ key, value }) => {
      if (value) {
        sessionStorage.setItem(key, value);
      }
    });
  }, [
    urlDelsysCode,
    urlSource,
    urlCoverageType,
    urlSearch,
    urlSignIn,
    urlPortal,
    urlClientName,
    isGuestDeeplinkFlow,
  ]);

  // Guest deeplink flow
  useEffect(() => {
    if (isGuestDeeplinkFlow) {
      // redirect to select LOB if lob cookie is missing
      if (!urlLOB) {
        setGeoLocationToStorage({});
        sessionStorage.removeItem(
          Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION
        );
        window.location.href = '/guest-plan-selection/';
        return;
      }

      // redirect to TypeOfCarePicker if coverageType cookie is missing or SHOP LOB with dental/vision coverage type which will open a new window with an external link
      if (
        !urlCoverageType ||
        (urlLOB === 'SHOP' &&
          (urlCoverageType === ReverseCoverageTypesCodes.DENTAL ||
            urlCoverageType === ReverseCoverageTypesCodes.VISION))
      ) {
        setGeoLocationToStorage({});
        sessionStorage.removeItem(
          Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION
        );
        window.location.href = `/guest-plan-selection/select-coverage-type?planSelectionLob=${urlLOB}`;
        return;
      }

      // Always redirect to plan-selection when urlLOB and urlCoverageType are present and delsysCode is missing
      if (urlLOB && urlCoverageType) {
        const guestLocation = sessionStorage.getItem(
          Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION
        );
        if (guestLocation && urlDelsysCode) {
          const clientNameParam =
            urlClientName?.toLowerCase() === 'geha' ? '&clientName=geha' : '';
          if (urlSearch) {
            // redirect to search results page if search param is present
            window.location.href = `/guest-plan-selection/browse/results/?coverageType=${urlCoverageType}&search=${encodeURIComponent(
              urlSearch
            )}&psx-source=deeplink${clientNameParam}`;
          } else {
            // redirect to plan landing page if search param is not present
            window.location.href = `/guest-plan-selection/browse?coverageType=${urlCoverageType}&chipValue=All${clientNameParam}`;
          }
        } else {
          setGeoLocationToStorage({});
          sessionStorage.removeItem(
            Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION
          );
          // in all other cases, stay on plan selection page
          window.location.href = `/guest-plan-selection/plan-selection?planSelectionLob=${urlLOB}&coverageType=${
            urlCoverageType || 'M'
          }&chipValue=${chipValue}`;
          return;
        }
      } else {
        setGeoLocationToStorage({});
        sessionStorage.removeItem(
          Constants.STORAGE_KEYS.SESSION.GUEST_LOCATION
        );
      }
    }
  }, [isGuestDeeplinkFlow, urlLOB, urlCoverageType, urlDelsysCode, urlSearch]);

  const { portalData } = useContext(PortalContext);
  //const [portalLob, setPortalLob] = useState(mapPortalToLob(portalData?.portalName))

  // Check for lobOverride in portal config (useful for testing when specific LOB data isn't available)
  const lobOverride = portalData?.portalConfig?.lobOverride;

  const portalLob =
    lobOverride ||
    mapPortalToLob(
      portalData?.portalName,
      portalData?.portalMemberInfo?.memberInfo?.at(0)?.lineOfBusiness,
      portalData?.portalConfig?.isGuestUser
    );

  // Determine the correct LOB to use.
  // For guest users, avoid using session storage due to overloaded usage across contexts.
  // For non-guest users, continue current logic to prefer portalLob, then session storage, then cookies as fallback.
  const isGuestPlanSelection = window.location.href.includes(
    '/guest-plan-selection'
  );
  const targetLob = isGuestPlanSelection
    ? portalLob || getLOBTargetInCookies()
    : portalLob || lob || getLOBTargetInCookies();

  const isGuest = storage.session.get(Constants.STORAGE_KEYS.SESSION.IS_GUEST);
  const portalLocale = portalData?.portalConfig?.language;
  const locale = isGuest ? localeFromStorage : urlLocale;

  // Get supported languages from session storage (set by Lagoon) or fall back to potentially supported
  const supportedLanguagesFromStorage = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.SUPPORTED_LANGUAGES
  );
  const supportedLanguages = supportedLanguagesFromStorage
    ? JSON.parse(supportedLanguagesFromStorage)
    : getPotientiallySupportedLanguagesLocale(targetLob);

  const localeCode =
    getLanguage(portalLocale || locale, supportedLanguages)?.code || 'en';
  const parsedTargetLob = parsedLob(targetLob, getLOBTargetInCookies() || '');
  const parsedTargetApp = parsedApp(targetLob);

  // Widget skeleton defined at lower component, full app uses spinner
  const loadingIcon = isWidget ? null : (
    <div style={LOADER_CENTER_STYLES}>
      <LoadingSpinner ariaLoadingLabel="Importing data" isLoading />
    </div>
  );

  return (
    <LagoonProvider
      app={parsedTargetApp}
      env={lagoonEnv as string}
      loadingIcon={loadingIcon}
      lob={parsedTargetLob}
      locale={localeCode}
    >
      {children}
    </LagoonProvider>
  );
};

// http://localhost:3000/guest-plan-selection/browse?deeplink=eyJsYW5ndWFnZSI6ImVuLVVTIiwibG9iIjoiRUkiLCJjb3ZlcmFnZVR5cGUiOiJNIiwicmVjaXByb2NpdHlJZCI6IjUxLE5hdEFuY0NvbW1lcmNpYWwiLCJjbGllbnROYW1lIjoiR0VIQSIsInBsYW5OYW1lIjoiQ2hvaWNlIEFkdmFuY2VkIiwiY3VzdG9tQnJhbmRpbmciOiJ5ZXMiLCJjbHVzdGVyS2V5IjoiUlAwMDIyVUgxIiwicG9ydGFsIjoiVU1SIiwicHZyYyI6IjAwMDEvMDAwMSIsInNpZ25pbiI6ImVuYWJsZSJ9&planYear=2026
// http://localhost:3000/guest-plan-selection/browse?deeplink=eyJsYW5ndWFnZSI6ImVuLVVTIiwibG9iIjoiRUkiLCJjb3ZlcmFnZVR5cGUiOiJNIiwicmVjaXByb2NpdHlJZCI6IjQ2LE5hdEFuY0NvbW1lcmNpYWwiLCJjbGllbnROYW1lIjoiR0VIQSIsInBsYW5OYW1lIjoiU2VsZWN0IENvbG9yYWRvIiwiY3VzdG9tQnJhbmRpbmciOiJ5ZXMiLCJjbHVzdGVyS2V5IjoiUlAwMDIyVUgxIiwicG9ydGFsIjoiVU1SIiwicHZyYyI6IjAwMDEvMDAwMSIsInNpZ25pbiI6ImVuYWJsZSJ9&planYear=2026
